"use client";

import { type ReactNode, useEffect, useRef } from "react";
import { useTranslations } from "use-intl";

import { SouthIcon } from "@/components/appIcons";
import { ScrollFade } from "@/components/ui/scroll-fade";
import { useActiveSection } from "@/hooks/use-active-section";
import { useFormatNumber } from "@/hooks/use-format-number";
import { useStickyHeaderHeight } from "@/hooks/use-sticky-header-height";
import { cn } from "@/lib/utils";

export interface PageJumpNavItem {
  /** Id of the section element the link scrolls to. */
  id: string;
  label: string;
  count?: number | null;
}

interface PageJumpNavProps {
  items: PageJumpNavItem[];
  /** Optional link rendered at the end of the row from `md` up. */
  trailing?: ReactNode;
  /**
   * `inset` (default) sits inside the padded detail-page shell and bleeds to its
   * edges; `fullBleed` spans an edge-to-edge page and centers its own content.
   */
  layout?: "inset" | "fullBleed";
  className?: string;
}

const LAYOUT_CLASSES = {
  inset: {
    nav: "-mx-4 px-4 md:-mx-10 md:px-10 lg:-mx-20 lg:px-20",
    row: "",
  },
  fullBleed: {
    nav: "w-full",
    row: "mx-auto max-w-6xl px-4 md:px-14",
  },
} as const;

/**
 * "On this page" jump links for long detail pages. Deliberately not tabs:
 * every section stays rendered below, the pills only scroll to it and the dot
 * marks the section being read. Sticks right below the app header while scrolling;
 * sections using it need `scroll-mt-36` so anchors clear header + nav.
 * Mobile drops the label and relies on the scroll-hint fade + chevron.
 */
export function PageJumpNav({ items, trailing, layout = "inset", className }: PageJumpNavProps) {
  const t = useTranslations("shared.pageJumpNav");
  const headerHeight = useStickyHeaderHeight();
  const formatNumber = useFormatNumber();
  const activeId = useActiveSection(items.map((item) => item.id));
  const listRef = useRef<HTMLUListElement>(null);

  // Keep the active pill visible inside the horizontally scrolling row (mobile),
  // without moving the page itself.
  useEffect(() => {
    const list = listRef.current;
    const scroller = list?.parentElement;
    const active = list?.querySelector<HTMLElement>('[aria-current="location"]');
    if (!scroller || !active) return;
    const start = active.offsetLeft;
    const end = start + active.offsetWidth;
    const viewStart = scroller.scrollLeft;
    const viewEnd = viewStart + scroller.clientWidth;
    if (start >= viewStart && end <= viewEnd) return;
    scroller.scrollTo({ left: Math.max(0, start - 16), behavior: "smooth" });
  }, [activeId]);

  if (items.length < 2) return null;

  return (
    <nav
      aria-label={t("label")}
      style={{ top: headerHeight }}
      className={cn(
        "sticky z-30 border-y border-border/70 bg-background/95 backdrop-blur-sm",
        LAYOUT_CLASSES[layout].nav,
        className,
      )}
    >
      <div className={cn("flex min-h-14 items-center gap-3", LAYOUT_CLASSES[layout].row)}>
        <span className="hidden shrink-0 text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground md:inline">
          {t("label")}
        </span>
        <ScrollFade scrollHint className="min-w-0 flex-1">
          <ul ref={listRef} className="flex w-max items-center gap-2 py-2.5">
            {items.map((item) => {
              const isActive = item.id === activeId;
              return (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    aria-current={isActive ? "location" : undefined}
                    className={cn(
                      "inline-flex h-11 items-center gap-1.5 whitespace-nowrap rounded-full sm:h-9 border border-border px-3 text-sm font-semibold text-foreground transition-colors hover:bg-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
                      isActive && "bg-secondary",
                    )}
                  >
                    {isActive && (
                      <span aria-hidden="true" className="size-1.5 rounded-full bg-primary" />
                    )}
                    {item.label}
                    {item.count != null && item.count > 0 && (
                      <span className="font-medium tabular-nums text-muted-foreground">
                        {formatNumber(item.count)}
                      </span>
                    )}
                    <SouthIcon className="text-[13px] text-muted-foreground" decorative />
                  </a>
                </li>
              );
            })}
          </ul>
        </ScrollFade>
        {trailing && <div className="hidden min-w-0 max-w-xs shrink md:flex">{trailing}</div>}
      </div>
    </nav>
  );
}
